import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, RefreshCw, Loader2, X, Clock, Inbox, CalendarDays } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { RequestRow } from '../components/requests/RequestRow';
import { RequestForm } from '../components/requests/RequestForm';
import apiService from '../services/api';

const STATUS_TABS = ['pending', 'approved', 'rejected', 'all'];

// When: upcoming = still to come or happening now; past = finished
const TIME_SCOPES = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past',     label: 'Past' },
  { key: 'any',      label: 'Any time' },
];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dayOf  = r => String(r.from ?? '').slice(0, 10);
const endOf  = r => String(r.to ?? r.from ?? '').slice(0, 10);
const monthKey = r => dayOf(r).slice(0, 7); // yyyy-mm
function monthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return 'No date';
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

// ── Department lookup ─────────────────────────────────────────
// Builds a Map<employeeId, Set<deptName>> from the departments list
// returned by GET /departments → [{ id, name, members: [{id, ...}] }]
// Also falls back to fields on the employee object itself for compatibility.
function buildEmpDeptMap(departments) {
  const map = new Map(); // employeeId → Set<deptName>
  departments.forEach(dept => {
    if (!dept.name) return;
    (dept.members ?? []).forEach(member => {
      const uid = member.id;
      if (!uid) return;
      if (!map.has(uid)) map.set(uid, new Set());
      map.get(uid).add(dept.name);
    });
  });
  return map;
}

// Get all dept names for a given employee, merging the prebuilt map
// with any fallback fields on the employee object itself.
function getEmpDeptNames(emp, empDeptMap) {
  if (!emp) return new Set();
  const uid   = emp.id;
  const names = new Set(uid && empDeptMap.has(uid) ? empDeptMap.get(uid) : []);
  if (emp.dept)             names.add(emp.dept);
  if (emp.department?.name) names.add(emp.department.name);
  (emp.departments ?? []).forEach(d => {
    if (d.departmentName) names.add(d.departmentName);
    if (d.name)           names.add(d.name);
  });
  return names;
}

export default function Requests() {
  const { employees, isAdmin, approvalScope } = useApp();
  // Department admins see their own department(s) here; company admins see everyone
  const teamOnly = !isAdmin;

  const [requests,    setRequests]   = useState([]);
  const [leaveTypes,  setLeaveTypes] = useState([]);
  const [departments, setDepts]      = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState(null);

  const [showForm,   setShowForm]   = useState(false);
  const [activeTab,  setActiveTab]  = useState('pending');
  const [timeScope,  setTimeScope]  = useState('upcoming');
  const [search,     setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [deptFilter, setDeptFilter] = useState('All');
  const searchRef = useRef(null);
  const pickedDefaultTab = useRef(false);

  // ── Fetch ─────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [requestsRes, typesRes, deptsRes] = await Promise.all([
        teamOnly
          ? apiService.getApprovalRequests({ limit: 500 })
          : apiService.getLeaveRequests({ limit: 200 }),
        apiService.getLeaveTypes(),
        apiService.getDepartments(),
      ]);

      const reqs = requestsRes?.data?.requests ?? requestsRes?.data ?? requestsRes ?? [];
      setRequests(Array.isArray(reqs) ? reqs : []);

      const types = typesRes?.data ?? typesRes ?? [];
      setLeaveTypes(Array.isArray(types) ? types : []);

      const depts = deptsRes?.data ?? deptsRes ?? [];
      setDepts(Array.isArray(depts) ? depts : []);
    } catch (err) {
      console.error('Requests fetch error:', err);
      setError('Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [teamOnly]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // The department filter only lists departments this person can see requests for
  const deptOptions = useMemo(() => {
    if (!teamOnly) return departments;
    const managed = new Set((approvalScope?.departments ?? []).map(d => d.id));
    return departments.filter(d => managed.has(d.id));
  }, [departments, teamOnly, approvalScope]);

  // On first load, open on "Pending" if anything is waiting, otherwise "All"
  useEffect(() => {
    if (loading || pickedDefaultTab.current) return;
    pickedDefaultTab.current = true;
    if (!requests.some(r => r.status === 'pending')) setActiveTab('all');
  }, [loading, requests]);

  // "/" jumps to search, Escape clears it
  useEffect(() => {
    function onKey(e) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (showForm) return;
      e.preventDefault();
      searchRef.current?.focus();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForm]);

  // ── New request submitted ─────────────────────────────────
  function handleRequestAdded(newReq) {
    if (!newReq?.id) return;
    setRequests(prev => {
      if (prev.some(r => r.id === newReq.id)) return prev;
      return [newReq, ...prev];
    });
  }

  // ── Approve / reject updated in place ─────────────────────
  function handleRequestUpdated(updatedReq) {
    setRequests(prev =>
      prev.map(r => r.id === updatedReq.id ? { ...r, ...updatedReq } : r)
    );
  }

  // ── Hard delete — remove row immediately ──────────────────
  function handleRequestDeleted(id) {
    setRequests(prev => prev.filter(r => r.id !== id));
  }

  // ── Filtering ─────────────────────────────────────────────
  const empDeptMap = useMemo(() => buildEmpDeptMap(departments), [departments]);
  const empById = useMemo(() => new Map(employees.map(e => [e.id, e])), [employees]);
  const empFor = useCallback(
    r => r.employee ?? r.emp ?? empById.get(r.empId ?? r.userId),
    [empById]
  );

  // Everything except the status tab, so the tab counts match what you'd see
  const scoped = useMemo(() => {
    const today = todayStr();
    const q = search.trim().toLowerCase();
    return requests.filter(r => {
      const emp = empFor(r);

      if (timeScope === 'upcoming' && endOf(r) < today) return false;
      if (timeScope === 'past'     && endOf(r) >= today) return false;

      if (typeFilter !== 'All' && r.type !== typeFilter) return false;

      const deptNames = getEmpDeptNames(emp, empDeptMap);
      if (deptFilter !== 'All' && !deptNames.has(deptFilter)) return false;

      // Search — name, leave type, department or note
      if (q) {
        const name = emp ? (emp.name ?? `${emp.firstName ?? ''} ${emp.lastName ?? ''}`).toLowerCase() : '';
        const hay = [name, (r.type ?? '').toLowerCase(), [...deptNames].join(' ').toLowerCase(), (r.note ?? '').toLowerCase()];
        if (!hay.some(h => h.includes(q))) return false;
      }
      return true;
    });
  }, [requests, empFor, timeScope, search, typeFilter, deptFilter, empDeptMap]);

  const counts = useMemo(() => ({
    all:      scoped.length,
    pending:  scoped.filter(r => r.status === 'pending').length,
    approved: scoped.filter(r => r.status === 'approved').length,
    rejected: scoped.filter(r => r.status === 'rejected').length,
  }), [scoped]);

  // Upcoming: soonest first. Past and any time: most recent first.
  const filtered = useMemo(() => {
    const list = activeTab === 'all' ? scoped : scoped.filter(r => r.status === activeTab);
    const dir = timeScope === 'upcoming' ? 1 : -1;
    return [...list].sort((a, b) => dir * dayOf(a).localeCompare(dayOf(b)));
  }, [scoped, activeTab, timeScope]);

  // Rows grouped under month headings
  const groups = useMemo(() => {
    const out = [];
    filtered.forEach(r => {
      const k = monthKey(r);
      const last = out[out.length - 1];
      if (last && last.key === k) last.items.push(r);
      else out.push({ key: k, items: [r] });
    });
    return out;
  }, [filtered]);

  // Each leave type's colour from company settings, for the type pill on each row.
  const typeColors = useMemo(
    () => Object.fromEntries(leaveTypes.map(t => [t.name, t.color])),
    [leaveTypes]
  );

  // All pending requests, whatever the filters — for the reminder banner
  const pendingTotal = useMemo(() => requests.filter(r => r.status === 'pending').length, [requests]);

  const activeFilters = [
    search.trim() && { key: 'search', label: `“${search.trim()}”`, clear: () => setSearch('') },
    typeFilter !== 'All' && { key: 'type', label: typeFilter, clear: () => setTypeFilter('All') },
    deptFilter !== 'All' && { key: 'dept', label: deptFilter, clear: () => setDeptFilter('All') },
  ].filter(Boolean);
  const clearAll = () => { setSearch(''); setTypeFilter('All'); setDeptFilter('All'); };

  const scopeWord = { upcoming: 'upcoming', past: 'past', any: '' }[timeScope];
  const emptyText = (() => {
    if (activeFilters.length) return 'No requests match these filters.';
    if (activeTab === 'pending') return `Nothing waiting for approval${scopeWord ? ` in ${scopeWord} leave` : ''}.`;
    if (activeTab === 'approved') return `No approved ${scopeWord} leave.`.replace('  ', ' ');
    if (activeTab === 'rejected') return `No rejected ${scopeWord} requests.`.replace('  ', ' ');
    return timeScope === 'upcoming' ? 'No upcoming leave booked.' : 'No requests yet.';
  })();

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <style>{`
        .rq-filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex: 1 1 auto; min-width: 0; }
        .rq-search { position: relative; flex: 0 1 260px; min-width: 180px; }
        @media (max-width: 640px) { .rq-search { flex: 1 1 100%; } }
        .rq-row2 { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .rq-chips { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .rq-chip { display: inline-flex; align-items: center; gap: 4px; height: 26px; padding: 0 6px 0 10px; border-radius: 13px; border: 1px solid var(--border); background: var(--surface); color: var(--text-2); font-size: 12.5px; font-weight: 500; cursor: pointer; }
        .rq-chip:hover { border-color: var(--field-border); color: var(--text); }
        .rq-chip svg { color: var(--text-muted); }
        .rq-link { border: none; background: none; padding: 0 4px; color: var(--accent); font-size: 12.5px; font-weight: 600; cursor: pointer; }
        .rq-banner { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 12px; background: var(--warn-bg-soft); border: 1px solid var(--warn-border); color: var(--warn-text); font-size: 13px; }
        .rq-banner b { font-weight: 700; }
        .rq-banner button { margin-left: auto; height: 30px; padding: 0 12px; border-radius: 8px; border: 1px solid var(--warn-border); background: var(--surface); color: var(--warn-text); font-size: 12.5px; font-weight: 600; cursor: pointer; }
        .rq-banner button:hover { background: var(--warn-bg); }
        .rq-month td { padding: 9px 16px 7px; background: var(--surface-2); border-top: 1px solid var(--border-soft); font-size: 11.5px; font-weight: 700; color: var(--text-2); text-transform: uppercase; letter-spacing: 0.05em; }
        .rq-month td span { margin-left: 8px; font-weight: 600; color: var(--text-muted); text-transform: none; letter-spacing: 0; }
        .rq-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 52px 20px; color: var(--text-muted); font-size: 13.5px; text-align: center; }
        .rq-empty svg { opacity: 0.45; margin-bottom: 4px; }
        .rq-kbd { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; padding: 0 4px; border-radius: 4px; border: 1px solid var(--border); background: var(--surface-2); color: var(--text-muted); font-size: 10.5px; font-weight: 600; position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; }
      `}</style>

      {/* Toolbar: search and filters on the left, actions on the right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="rq-filters">
          <div className="rq-search">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 th-muted" />
            <input
              ref={searchRef}
              type="search"
              className="form-input pl-8"
              style={{ width: '100%', paddingRight: search ? 12 : 34 }}
              placeholder="Search people or types…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); e.currentTarget.blur(); } }}
              aria-label="Search requests"
            />
            {!search && <span className="rq-kbd" aria-hidden="true">/</span>}
          </div>

          <select className="form-input" style={{ width: 'auto' }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)} aria-label="Leave type">
            <option value="All">All types</option>
            {leaveTypes.map(t => <option key={t.id ?? t.name} value={t.name}>{t.name}</option>)}
          </select>

          <select className="form-input" style={{ width: 'auto' }} value={deptFilter} onChange={e => setDeptFilter(e.target.value)} aria-label="Department">
            <option value="All">All departments</option>
            {deptOptions.map(d => <option key={d.id ?? d.name} value={d.name}>{d.name}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            disabled={loading}
            className="form-input flex items-center justify-center gap-1.5"
            style={{ width: 40, height: 40, padding: 0, cursor: 'pointer', color: 'var(--text-2)' }}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
            style={{ height: 40, flexShrink: 0 }}
          >
            <Plus size={15} strokeWidth={2.6} /> New request
          </button>
        </div>
      </div>

      {/* Status and time */}
      <div className="rq-row2">
        <div className="seg" role="group" aria-label="Filter by status">
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              type="button"
              aria-pressed={activeTab === tab}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span className="seg-count">{counts[tab]}</span>
            </button>
          ))}
        </div>

        <div className="seg" role="group" aria-label="Filter by date">
          {TIME_SCOPES.map(s => (
            <button key={s.key} type="button" aria-pressed={timeScope === s.key} onClick={() => setTimeScope(s.key)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active filters, each removable */}
      {activeFilters.length > 0 && (
        <div className="rq-chips">
          {activeFilters.map(f => (
            <button key={f.key} type="button" className="rq-chip" onClick={f.clear} title="Remove filter">
              {f.label} <X size={13} />
            </button>
          ))}
          {activeFilters.length > 1 && <button type="button" className="rq-link" onClick={clearAll}>Clear all</button>}
        </div>
      )}

      {/* Reminder when requests are waiting but you're looking elsewhere */}
      {!loading && pendingTotal > 0 && activeTab !== 'pending' && (
        <div className="rq-banner" role="status">
          <Clock size={15} />
          <span><b>{pendingTotal}</b> {pendingTotal === 1 ? 'request is' : 'requests are'} waiting for approval</span>
          <button type="button" onClick={() => { setActiveTab('pending'); setTimeScope('any'); clearAll(); }}>Review</button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div style={{ padding: '10px 14px', background: 'var(--bad-bg)', border: '1px solid var(--bad-border)', borderRadius: 12, fontSize: 13, color: 'var(--bad-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{error}</span>
          <button onClick={fetchAll} style={{ fontSize: 12, color: 'var(--bad-text)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60, gap: 10, color: 'var(--text-muted)' }}>
            <Loader2 size={20} className="animate-spin" />
            <span style={{ fontSize: 14 }}>Loading requests…</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 700 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                    {['Employee', 'Type', 'Dates', 'Days', 'Status', 'Note', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[11px] font-bold th-muted uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[color:var(--border-soft)]">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="rq-empty">
                          {activeTab === 'pending' && !activeFilters.length ? <Inbox size={24} /> : <CalendarDays size={24} />}
                          <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{emptyText}</span>
                          {activeFilters.length > 0 ? (
                            <button type="button" className="rq-link" onClick={clearAll}>Clear filters</button>
                          ) : timeScope !== 'any' ? (
                            <button type="button" className="rq-link" onClick={() => setTimeScope('any')}>Show any time</button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    groups.map(g => (
                      <React.Fragment key={g.key}>
                        <tr className="rq-month">
                          <td colSpan={7}>
                            {monthLabel(g.key)}
                            <span>{g.items.length} {g.items.length === 1 ? 'request' : 'requests'}</span>
                          </td>
                        </tr>
                        {g.items.map(r => (
                          <RequestRow
                            key={r.id}
                            request={r}
                            typeColor={typeColors[r.type]}
                            onUpdated={handleRequestUpdated}
                            onDeleted={handleRequestDeleted}
                          />
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 text-xs th-muted" style={{ borderTop: '1px solid var(--border-soft)' }}>
              Showing {filtered.length} of {requests.length} requests
              {timeScope === 'upcoming' && ' · soonest first'}
              {timeScope !== 'upcoming' && ' · most recent first'}
            </div>
          </>
        )}
      </div>

      {/* New request form */}
      {showForm && (
        <RequestForm
          onClose={() => setShowForm(false)}
          onRequestCreated={handleRequestAdded}
        />
      )}
    </div>
  );
}